import { PrimaryControlsStripTitlePublishingContext } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import type { LearnDeckSurface } from "@-/dev.improve.haniverse.lib.learn.deck";
import {
  LearnCardSurfaceContext,
  type LearnCardSurfaceContextValue,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useEffect, useMemo, type ReactNode } from "react";
import { isDiagnosticsRecording, markDiagnostics } from "@-/dev.improve.haniverse.lib.diagnostics";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import { StickyAccordionBottomFadeContext } from "@-/dev.improve.haniverse.modules.sticky-accordion";
import {
  VisualActivityProvider,
  VisualResourcePreparationContext,
} from "@-/dev.improve.haniverse.lib.visual-activity";

const STUDY_BOTTOM_FADE = { gap: 9, side: "left" } as const;
const INFO_BOTTOM_FADE = { gap: 9, side: "right" } as const;

/**
 * Marks a subtree as one surface of one card, and keeps inert surfaces inert: an inactive surface
 * cannot publish the header title, and everything reading `useLearnCardSurface().isActive` (input
 * focus, audio, voice turns, keyboard shortcuts) holds still. Visual resources follow the same
 * activity boundary while hidden surfaces remain mounted and can finish preparing their content.
 */
export function LearnFlashcardSurfaceProvider({
  children,
  frontVisibleSignal,
  isActive,
  isCurrent,
  onReady,
  presentationKey,
  surface,
}: {
  children: ReactNode;
  frontVisibleSignal?: SharedValue<boolean>;
  isActive: boolean;
  isCurrent: boolean;
  onReady: (surface: LearnDeckSurface) => void;
  presentationKey: string;
  surface: LearnDeckSurface;
}) {
  useEffect(() => {
    // Observe committed subtree lifetime, independently of focus and the planned preload budget.
    const record = (action: "mounted" | "unmounted") => {
      if (isDiagnosticsRecording()) {
        markDiagnostics(
          JSON.stringify({ action, kind: "learn-surface", presentationKey, surface }),
        );
      }
    };
    record("mounted");
    return () => record("unmounted");
  }, [presentationKey, surface]);
  const value = useMemo<LearnCardSurfaceContextValue>(
    () => ({
      frontVisibleSignal,
      isActive,
      isCurrent,
      presentationKey,
      reportReady: () => onReady(surface),
      surface,
    }),
    [frontVisibleSignal, isActive, isCurrent, onReady, presentationKey, surface],
  );
  return (
    <LearnCardSurfaceContext.Provider value={value}>
      <PrimaryControlsStripTitlePublishingContext.Provider value={isActive}>
        <StickyAccordionBottomFadeContext
          value={surface === "info" ? INFO_BOTTOM_FADE : STUDY_BOTTOM_FADE}
        >
          <VisualResourcePreparationContext value={surface !== "info"}>
            <VisualActivityProvider active={isActive}>{children}</VisualActivityProvider>
          </VisualResourcePreparationContext>
        </StickyAccordionBottomFadeContext>
      </PrimaryControlsStripTitlePublishingContext.Provider>
    </LearnCardSurfaceContext.Provider>
  );
}
